import { FormTypes } from "src/utils/types/inputTypes";
import { FutureData, SeriesData, Valuation } from "../../types/outputTypes";
import { getNameFromInput, getServiceFromInput } from "../helpers";
import { chartColors } from "src/utils/enums/chartColors";

const getValuation = (dataArr: Valuation) =>
  dataArr.map((obj) => ({
    x: new Date(obj.date),
    y: obj.value,
  }));

const getCashFlow = (dataArr: Valuation) =>
  dataArr.map((obj) => ({
    x: new Date(obj.date),
    y: obj.depositAcc,
  }));

/** Concatenate history + forecast arrays, dropping any forecast points that
 *  fall on or before the last history date (avoids overlap at the seam).
 *  Uses date-only comparison (ignoring time-of-day). */
const toDateOnly = (d: Date) =>
  Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());

const concatTimeSeries = (
  history: { x: Date; y: number }[],
  forecast: { x: Date; y: number }[]
) => {
  if (history.length === 0) return forecast;
  if (forecast.length === 0) return history;
  const lastDay = toDateOnly(history[history.length - 1].x);
  return [
    ...history,
    ...forecast.filter((pt) => toDateOnly(pt.x) > lastDay),
  ];
};

export const transformCombiData = ({
  data,
  input,
}: {
  data: FutureData;
  input: FormTypes[];
}) => {
  const portfolioData: { [key: string]: { [key: string]: SeriesData } } = {
    portfolio: {},
  };
  const serviceData: { [key: string]: { [key: string]: SeriesData } } = {};
  const productsData: { [key: string]: { [key: string]: SeriesData } } = {};

  const scenarioEnum = {
    negative: { label: "Negativní scénář", color: "#f33b12" },
    neutral: { label: "Neutrální scénář", color: "#f9b429" },
    positive: { label: "Pozitivní scénář", color: "#00a7b8" },
  };

  const scenarios: ("negative" | "neutral" | "positive")[] = [
    "negative",
    "neutral",
    "positive",
  ];
  const palette = chartColors;
  let paletteIndex = 0;

  scenarios.forEach((key) => {
    const isNeutral = key === "neutral";
    const forecastPortfolio =
      data.forecast[key as keyof typeof data.forecast].portfolio;

    // --- Portfolio level ---
    portfolioData.portfolio[key] = {
      id: `portfolio_${key}`,
      label: isNeutral
        ? "Hodnota / Neutrální scénář"
        : scenarioEnum[key].label,
      data: isNeutral
        ? concatTimeSeries(
            getValuation(data.history.portfolio.valuation),
            getValuation(forecastPortfolio.valuation)
          )
        : getValuation(forecastPortfolio.valuation),
      borderColor: isNeutral ? palette[paletteIndex] : scenarioEnum[key].color,
      order: 2,
      yAxisID: "valuation",
      setID: "portfolio",
      setLabel: "Portfolio",
      hidden: false,
      borderWidth: 2,
    };

    // --- Service level ---
    forecastPortfolio.byServices.forEach((service, serviceIndex) => {
      const serviceKey = `service_${service.serviceId}`;
      const serviceLabel = getServiceFromInput({
        serviceId: service.serviceId,
        input,
      });
      serviceData[serviceKey] = serviceData[serviceKey] || {};

      serviceData[serviceKey][key] = {
        id: `${serviceKey}_${key}`,
        label: isNeutral
          ? "Hodnota / Neutrální scénář"
          : scenarioEnum[key].label,
        data: isNeutral
          ? concatTimeSeries(
              getValuation(
                data.history.portfolio.byServices[serviceIndex].valuation
              ),
              getValuation(service.valuation)
            )
          : getValuation(service.valuation),
        borderColor: scenarioEnum[key].color,
        order: 2,
        yAxisID: "valuation",
        setID: serviceKey,
        setLabel: `${serviceLabel}`,
        hidden: false,
        borderWidth: 2,
      };

      // --- Product level ---
      service.byProducts.forEach((product, productIndex) => {
        const productKey = `product_${service.serviceId}_${product.productId}`;
        const productLabel = getNameFromInput({
          productId: product.productId,
          serviceId: service.serviceId,
          input,
        });
        productsData[productKey] = productsData[productKey] || {};

        productsData[productKey][key] = {
          id: `${productKey}_${key}`,
          label: isNeutral
            ? "Hodnota / Neutrální scénář"
            : scenarioEnum[key].label,
          data: isNeutral
            ? concatTimeSeries(
                getValuation(
                  data.history.portfolio.byServices[serviceIndex].byProducts[
                    productIndex
                  ].valuation
                ),
                getValuation(product.valuation)
              )
            : getValuation(product.valuation),
          borderColor: scenarioEnum[key].color,
          order: 3,
          yAxisID: "valuation",
          setID: productKey,
          hidden: false,
          setLabel: `${productLabel}`,
          borderWidth: 2,
        };
      });
    });
  });

  // --- CashFlow series (full timeline, stepped dashed) ---
  portfolioData.portfolio.cashFlow = {
    id: "portfolio_cashFlow",
    label: "Cashflow",
    data: concatTimeSeries(
      getCashFlow(data.history.portfolio.valuation),
      getCashFlow(data.forecast.neutral.portfolio.valuation)
    ),
    borderColor: "#5f6c6e",
    order: 2,
    yAxisID: "valuation",
    setID: "portfolio",
    setLabel: "Portfolio",
    hidden: false,
    borderWidth: 2,
    borderDash: [5, 2],
    stepped: true,
  };

  data.history.portfolio.byServices.forEach((service, serviceIndex) => {
    const serviceKey = `service_${service.serviceId}`;
    const serviceLabel = getServiceFromInput({
      serviceId: service.serviceId,
      input,
    });
    serviceData[serviceKey] = serviceData[serviceKey] || {};
    paletteIndex = (paletteIndex + 1) % palette.length;

    serviceData[serviceKey].cashFlow = {
      id: `service_${service.serviceId}_cashFlow`,
      label: "CashFlow",
      data: concatTimeSeries(
        getCashFlow(service.valuation),
        getCashFlow(
          data.forecast.neutral.portfolio.byServices[serviceIndex].valuation
        )
      ),
      order: 2,
      yAxisID: "valuation",
      setID: serviceKey,
      setLabel: `${serviceLabel}`,
      hidden: false,
      borderColor: palette[paletteIndex],
      borderWidth: 2,
      borderDash: [5, 2],
      stepped: true,
    };

    service.byProducts.forEach((product, productIndex) => {
      const productKey = `product_${service.serviceId}_${product.productId}`;
      const productLabel = getNameFromInput({
        productId: product.productId,
        serviceId: service.serviceId,
        input,
      });
      paletteIndex = (paletteIndex + 1) % palette.length;

      productsData[productKey].cashFlow = {
        id: `${productKey}_cashFlow`,
        label: "CashFlow",
        data: concatTimeSeries(
          getCashFlow(product.valuation),
          getCashFlow(
            data.forecast.neutral.portfolio.byServices[serviceIndex].byProducts[
              productIndex
            ].valuation
          )
        ),
        order: 3,
        yAxisID: "valuation",
        setID: productKey,
        hidden: false,
        setLabel: `${productLabel}`,
        borderColor: palette[paletteIndex],
        borderWidth: 2,
        borderDash: [5, 2],
        stepped: true,
      };
    });
  });

  return {
    minMax_X: { min: new Date(data.from), max: new Date(data.to) },
    data: {
      portfolioData,
      serviceData,
      productsData,
    },
  };
};
